import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  IApiPaginatedMeta,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  IAddTaskCommentPayload,
  IBulkCreatePreview,
  IBulkCreateResult,
  ICreateTaskPayload,
  ICreateWorklogPayload,
  IListTasksParams,
  IMoveTaskPayload,
  ISingleTask,
  ITaskAttachment,
  ITaskBoard,
  ITaskBoardColumn,
  ITaskComment,
  ITaskUser,
  IUpdateTaskPayload,
  IUpdateWorklogPayload,
} from "@/types/(my-space)/tasks.interface";
import type { ISingleProject } from "@/types/(company)/projects.interface";
import { createApi } from "@reduxjs/toolkit/query/react";
import { applyOptimisticMove } from "@/utils/optimistic-task-move";

const TASKS = APIEndpoints.MySpace.Tasks;

export const msTasksApi = createApi({
  reducerPath: "msTasksApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "MsBoardColumns",
    "MsTasks",
    "MsTask",
    "MsTaskComments",
    "MsMyProjects",
    "MsProjectBoard",
  ],
  endpoints: (builder) => ({
    // ── Projects I'm in ───────────────────────────────────────────────────────
    msGetMyProjects: builder.query<IApiResponse<ISingleProject[]>, void>({
      query: () => ({ url: TASKS.MyProjects }),
      providesTags: ["MsMyProjects"],
    }),
    msGetProjectBoard: builder.query<
      IApiResponse<ITaskBoard>,
      { projectId: number }
    >({
      query: ({ projectId }) => ({ url: TASKS.ProjectBoard(projectId) }),
      providesTags: ["MsProjectBoard"],
    }),

    // ── Board columns / members (any board the caller can access) ────────────
    // Columns for any board the caller can access (status picker on detail).
    msGetBoardColumnsByBoard: builder.query<
      IApiResponse<ITaskBoardColumn[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardColumnsByBoard(boardId) }),
      providesTags: ["MsBoardColumns"],
    }),
    // Full member roster of an accessible board (assignee filter).
    msGetBoardMembersByBoard: builder.query<
      IApiResponse<ITaskUser[]>,
      { boardId: number }
    >({
      query: ({ boardId }) => ({ url: TASKS.BoardMembersByBoard(boardId) }),
      providesTags: ["MsBoardColumns"],
    }),

    // ── Tasks ───────────────────────────────────────────────────────────────
    msGetTasks: builder.query<
      IApiPaginatedResponse<ISingleTask[]>,
      IListTasksParams
    >({
      query: (params) => ({ url: TASKS.Tasks, params }),
      providesTags: ["MsTasks"],
    }),
    // Loops every page so the board/backlog/roadmap/reports never truncate.
    msGetAllTasks: builder.query<
      IApiPaginatedResponse<ISingleTask[]>,
      IListTasksParams
    >({
      async queryFn(params, _api, _extra, baseQuery) {
        const PER_PAGE = 500;
        const MAX_PAGES = 40;
        const all: ISingleTask[] = [];
        let meta: IApiPaginatedMeta | undefined;
        for (let page = 1; page <= MAX_PAGES; page++) {
          const res = await baseQuery({
            url: TASKS.Tasks,
            params: { ...params, page, limit: PER_PAGE },
          });
          if (res.error) return { error: res.error };
          const body = res.data as IApiPaginatedResponse<ISingleTask[]>;
          all.push(...(body.data ?? []));
          meta = body.meta;
          if (page >= (body.meta?.lastPage ?? 1)) break;
        }
        return {
          data: {
            success: true,
            statusCode: 200,
            message: "ok",
            data: all,
            meta: {
              ...(meta as IApiPaginatedMeta),
              total: all.length,
              totalRecords: all.length,
              limit: all.length,
            },
          },
        };
      },
      providesTags: ["MsTasks"],
    }),
    msGetTask: builder.query<IApiResponse<ISingleTask>, { taskId: number }>({
      query: ({ taskId }) => ({ url: TASKS.TaskById(taskId) }),
      providesTags: ["MsTask"],
    }),
    msCreateTask: builder.mutation<IApiResponse<ISingleTask>, ICreateTaskPayload>({
      query: (body) => ({ url: TASKS.Tasks, method: "POST", body }),
      // Invalidate the open task too — subtasks render on the parent's detail.
      invalidatesTags: ["MsTasks", "MsTask"],
    }),
    msBulkCreateTasks: builder.mutation<
      IApiResponse<IBulkCreatePreview | IBulkCreateResult>,
      FormData
    >({
      query: (body) => ({ url: TASKS.BulkCreateTasks, method: "POST", body }),
      invalidatesTags: ["MsTasks", "MsTask"],
    }),
    msUpdateTask: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: IUpdateTaskPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.TaskById(taskId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["MsTasks", "MsTask"],
    }),
    msMoveTask: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: IMoveTaskPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.MoveTask(taskId),
        method: "PATCH",
        body: payload,
      }),
      // Optimistically reposition the card so the board updates instantly — no
      // refetch flicker. Patches every cached task list (any search/filter args);
      // the invalidation below still reconciles with server truth afterwards.
      async onQueryStarted(
        { taskId, payload },
        { dispatch, queryFulfilled, getState },
      ) {
        const entries = msTasksApi.util
          .selectInvalidatedBy(getState(), [{ type: "MsTasks" }])
          .filter(
            (e) =>
              e.endpointName === "msGetTasks" ||
              e.endpointName === "msGetAllTasks",
          );
        const patches = entries.map((e) =>
          dispatch(
            msTasksApi.util.updateQueryData(
              e.endpointName as "msGetTasks" | "msGetAllTasks",
              e.originalArgs as IListTasksParams,
              (draft) => {
                applyOptimisticMove(draft.data, taskId, payload);
              },
            ),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: ["MsTasks", "MsTask"],
    }),
    msDeleteTask: builder.mutation<IApiResponse<void>, { taskId: number }>({
      query: ({ taskId }) => ({ url: TASKS.TaskById(taskId), method: "DELETE" }),
      invalidatesTags: ["MsTasks"],
    }),

    // ── Comments ────────────────────────────────────────────────────────────
    msGetTaskComments: builder.query<
      IApiResponse<ITaskComment[]>,
      { taskId: number }
    >({
      query: ({ taskId }) => ({ url: TASKS.TaskComments(taskId) }),
      providesTags: ["MsTaskComments"],
    }),
    msAddTaskComment: builder.mutation<
      IApiResponse<ITaskComment>,
      { taskId: number; payload: IAddTaskCommentPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.TaskComments(taskId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["MsTaskComments", "MsTask"],
    }),
    msUpdateTaskComment: builder.mutation<
      IApiResponse<ITaskComment>,
      { taskId: number; commentId: number; payload: IAddTaskCommentPayload }
    >({
      query: ({ taskId, commentId, payload }) => ({
        url: TASKS.TaskCommentById(taskId, commentId),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["MsTaskComments"],
    }),
    msDeleteTaskComment: builder.mutation<
      IApiResponse<{ id: number }>,
      { taskId: number; commentId: number }
    >({
      query: ({ taskId, commentId }) => ({
        url: TASKS.TaskCommentById(taskId, commentId),
        method: "DELETE",
      }),
      invalidatesTags: ["MsTaskComments", "MsTask"],
    }),

    // ── Watchers ──────────────────────────────────────────────────────────────
    msWatchTask: builder.mutation<IApiResponse<{ watching: boolean }>, { taskId: number }>({
      query: ({ taskId }) => ({ url: TASKS.TaskWatch(taskId), method: "POST" }),
      invalidatesTags: ["MsTask"],
    }),
    msUnwatchTask: builder.mutation<IApiResponse<{ watching: boolean }>, { taskId: number }>({
      query: ({ taskId }) => ({
        url: TASKS.TaskWatch(taskId),
        method: "DELETE",
      }),
      invalidatesTags: ["MsTask"],
    }),

    // ── Archive ───────────────────────────────────────────────────────────────
    msArchiveTask: builder.mutation<IApiResponse<ISingleTask>, { taskId: number }>({
      query: ({ taskId }) => ({
        url: TASKS.TaskArchive(taskId),
        method: "POST",
      }),
      invalidatesTags: ["MsTask", "MsTasks"],
    }),
    msUnarchiveTask: builder.mutation<IApiResponse<ISingleTask>, { taskId: number }>({
      query: ({ taskId }) => ({
        url: TASKS.TaskUnarchive(taskId),
        method: "POST",
      }),
      invalidatesTags: ["MsTask", "MsTasks"],
    }),

    // ── Time tracking ─────────────────────────────────────────────────────────
    msAddWorklog: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; payload: ICreateWorklogPayload }
    >({
      query: ({ taskId, payload }) => ({
        url: TASKS.Worklogs(taskId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["MsTask", "MsTasks"],
    }),
    msUpdateWorklog: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; worklogId: number; payload: IUpdateWorklogPayload }
    >({
      query: ({ taskId, worklogId, payload }) => ({
        url: TASKS.WorklogById(taskId, worklogId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["MsTask", "MsTasks"],
    }),
    msDeleteWorklog: builder.mutation<
      IApiResponse<ISingleTask>,
      { taskId: number; worklogId: number }
    >({
      query: ({ taskId, worklogId }) => ({
        url: TASKS.WorklogById(taskId, worklogId),
        method: "DELETE",
      }),
      invalidatesTags: ["MsTask", "MsTasks"],
    }),

    // ── Attachments ───────────────────────────────────────────────────────────
    msUploadTaskAttachments: builder.mutation<
      IApiResponse<ITaskAttachment[]>,
      FormData
    >({
      query: (body) => ({
        url: APIEndpoints.Common.TaskAttachmentUpload,
        method: "POST",
        body,
      }),
    }),
    /** Chat files → unattached task attachments (claimed at create), in id order. */
    msCopyChatAttachments: builder.mutation<
      IApiResponse<ITaskAttachment[]>,
      { chatAttachmentIds: number[] }
    >({
      query: (body) => ({
        url: APIEndpoints.Common.TaskAttachmentFromChat,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useMsGetMyProjectsQuery,
  useMsGetProjectBoardQuery,
  useMsGetBoardColumnsByBoardQuery,
  useMsGetBoardMembersByBoardQuery,
  useMsGetTasksQuery,
  useMsGetAllTasksQuery,
  useMsGetTaskQuery,
  useMsCreateTaskMutation,
  useMsBulkCreateTasksMutation,
  useMsUpdateTaskMutation,
  useMsMoveTaskMutation,
  useMsDeleteTaskMutation,
  useMsGetTaskCommentsQuery,
  useMsAddTaskCommentMutation,
  useMsUpdateTaskCommentMutation,
  useMsDeleteTaskCommentMutation,
  useMsWatchTaskMutation,
  useMsUnwatchTaskMutation,
  useMsArchiveTaskMutation,
  useMsUnarchiveTaskMutation,
  useMsAddWorklogMutation,
  useMsUpdateWorklogMutation,
  useMsDeleteWorklogMutation,
  useMsUploadTaskAttachmentsMutation,
  useMsCopyChatAttachmentsMutation,
} = msTasksApi;
